/**
 * HomeLoadProgress — step checklist shown on the landing page while an
 * existing draft is being loaded into Word.
 *
 * Selecting a document in HomeScene's "Your documents" list kicks off a
 * multi-second sequence (download the .docx → rebuild it in Word → restore
 * the session → load the spine). Before this component the only feedback
 * was a `loading` flag that HomeDocCard used solely to disable clicks, so
 * the writer saw a frozen screen until the spine suddenly appeared. This
 * card narrates each step so the wait is legible right up until the spine
 * unmounts the landing page.
 *
 * Pure presentation surface — the parent (HomeScene) owns the step model
 * and advances it across `handleLoadDoc`'s awaits. Visual language mirrors
 * LoomSpine's "Preparing run" init checklist (done/active/pending markers).
 *
 * SRP: no fetches, no Office.js, no state. Every branch is unit-testable
 * from plain props.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../tokens";

export type LoadStepState = "done" | "active" | "pending";

export interface LoadStep {
  /** Stable key for React + tests. */
  id: string;
  /** Human-readable label ("Downloading your draft…"). */
  label: string;
  state: LoadStepState;
}

// Ordered setup steps for loading an existing draft. Each entry maps to
// one await block in ``HomeScene.handleLoadDoc``; the active index names
// the step currently in flight. The final step ("Loading the spine")
// resolves when ``onRunLoaded`` returns — at which point the shell
// transitions out of idle and the whole landing scene unmounts into the
// ready spine. Lives here (a pure module) so it's testable without
// pulling HomeScene's Office.js imports into the test.
export const LOAD_STEP_DEFS: readonly { id: string; label: string }[] = [
  { id: "prepare", label: "Preparing" },
  { id: "download", label: "Downloading your draft" },
  { id: "rebuild", label: "Rebuilding in Word" },
  { id: "restore", label: "Restoring session" },
  { id: "spine", label: "Loading the spine" },
] as const;

/** Project the active-step index into per-step render state. Indices
 *  before ``activeIndex`` are done, the active one is active, later ones
 *  pending. On failure the parent keeps the failed step at ``activeIndex``
 *  and passes ``error`` so HomeLoadProgress renders it as the failed step.
 *
 *  When the "rebuild" step is active and ``progress`` is supplied (the
 *  scaffold-then-fill Load path emits one tick per section written), its
 *  label gains an ``x / total`` counter so the writer sees sections landing
 *  on a large doc instead of an apparent freeze — mirrors ``buildSaveSteps``. */
export function buildLoadSteps(
  activeIndex: number,
  progress?: { current: number; total: number } | null,
): LoadStep[] {
  return LOAD_STEP_DEFS.map((def, i) => {
    let label = def.label;
    if (def.id === "rebuild" && i === activeIndex && progress && progress.total > 0) {
      label = `${def.label} (${progress.current}/${progress.total})`;
    }
    return {
      id: def.id,
      label,
      state: i < activeIndex ? "done" : i === activeIndex ? "active" : "pending",
    };
  });
}

// Ordered steps for the Save button's whole-document sweep. Each maps to a
// phase of ``VariantCShell.handleSave``: prepare → read (the single shared,
// text-only Word scan) → save (per-section persistence, with an x/total
// counter) → issues (re-fetch the reconciled issue ledger). Reuses the same
// pure-projection model as ``buildLoadSteps`` so the shared card renders it.
export const SAVE_STEP_DEFS: readonly { id: string; label: string }[] = [
  { id: "prepare", label: "Preparing" },
  { id: "read", label: "Reading your document" },
  { id: "save", label: "Saving changes" },
  { id: "issues", label: "Updating issues" },
] as const;

/** Project the active-step index into Save steps. The whole save is now a single
 *  bulk request (no per-section acks), so progress is shown by the percent bar
 *  rather than an ``x / total`` counter on the label. */
export function buildSaveSteps(activeIndex: number): LoadStep[] {
  return SAVE_STEP_DEFS.map((def, i) => ({
    id: def.id,
    label: def.label,
    state: i < activeIndex ? "done" : i === activeIndex ? "active" : "pending",
  }));
}

// Ordered steps for the "Build on this document" (custom-submission) import.
// Each maps to a phase of ``VariantCShell.startCustomFlow``: read the live
// doc's heading outline → register the run server-side → build the spine from
// the outline → hydrate the sections into the shell. Reuses the same pure
// projection as ``buildLoadSteps`` so the shared card narrates the wait —
// the live-run "booting" checklist is deliberately suppressed for custom runs.
export const CUSTOM_IMPORT_STEP_DEFS: readonly { id: string; label: string }[] = [
  { id: "outline", label: "Reading document outline" },
  { id: "project", label: "Creating project" },
  { id: "spine", label: "Building the spine" },
  { id: "sections", label: "Loading sections" },
] as const;

/** Project the active-step index into custom-import steps. Same done/active/
 *  pending model as {@link buildLoadSteps}; the parent keeps a failed step at
 *  ``activeIndex`` and passes ``error`` for the failed frame. */
export function buildCustomImportSteps(activeIndex: number): LoadStep[] {
  return CUSTOM_IMPORT_STEP_DEFS.map((def, i) => ({
    id: def.id,
    label: def.label,
    state: i < activeIndex ? "done" : i === activeIndex ? "active" : "pending",
  }));
}

interface HomeLoadProgressProps {
  /** Title of the document being opened — echoed so the writer knows
   *  which card they clicked. Omit to hide the title line entirely. */
  docTitle?: string;
  /** Ordered steps with their current state. */
  steps: LoadStep[];
  /** Non-null when the flow failed — swaps the pulsing header for a
   *  static error and shows the message below the steps. */
  error?: string | null;
  /** Terminal success frame — swaps the ⧗ header for a ✓ and stops pulsing
   *  (the caller passes all-``done`` steps). Ignored when ``error`` is set. */
  done?: boolean;
  /** In-flight header (default "Opening document"). */
  header?: string;
  /** Error header (default "Couldn't open document"). */
  errorHeader?: string;
  /** Success header shown when ``done`` (default falls back to ``header``). */
  doneHeader?: string;
  /** Dismiss-button label (default "← Back to documents"). */
  dismissLabel?: string;
  /** Shown only on the error/done path so the writer can dismiss. Omit while
   *  the flow is in flight (no cancel affordance). */
  onDismiss?: () => void;
  /** 0–100 determinate progress. When supplied (and not in the error frame) a
   *  thin bar renders under the steps. Used by the Save card, where the whole
   *  save is one request so there is no per-step counter. */
  percent?: number | null;
}

const useStyles = makeStyles({
  panel: {
    backgroundColor: "var(--surface-raised)",
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "8px",
    padding: "14px 14px 13px",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 1px 2px rgba(76,51,138,0.04)",
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontFamily: fonts.mono,
    fontSize: "10px",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: inkColors.ink3,
    margin: 0,
  },
  titleIcon: {
    opacity: 0.6,
  },
  docTitle: {
    fontFamily: fonts.display,
    fontSize: "14px",
    fontWeight: 700,
    color: inkColors.ink,
    letterSpacing: "-0.2px",
    lineHeight: 1.25,
    margin: "6px 0 10px",
  },
  step: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "3px 0",
    fontFamily: fonts.body,
    fontSize: "11.5px",
  },
  stepMarker: {
    flexShrink: 0,
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    border: `1px solid ${inkColors.ink5}`,
    background: "transparent",
  },
  stepMarkerDone: {
    border: "none",
    background: statusColors.cool,
  },
  stepMarkerActive: {
    border: "none",
    background: brandColors.mid,
    boxShadow: `0 0 6px ${brandColors.glow}`,
    animationName: {
      "0%": { opacity: 1 },
      "50%": { opacity: 0.45 },
      "100%": { opacity: 1 },
    },
    animationDuration: "1600ms",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
    },
  },
  stepLabel: {
    color: inkColors.ink2,
  },
  stepLabelPending: {
    color: inkColors.ink4,
  },
  stepLabelError: {
    color: inkColors.ink4,
  },
  barTrack: {
    marginTop: "10px",
    height: "5px",
    borderRadius: "3px",
    backgroundColor: surfaceColors.line,
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    borderRadius: "3px",
    background: brandColors.mid,
    transitionProperty: "width",
    transitionDuration: "300ms",
    transitionTimingFunction: "ease",
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "0ms",
    },
  },
  errorRow: {
    marginTop: "10px",
    padding: "8px 12px",
    backgroundColor: "var(--danger-soft)",
    color: "var(--danger)",
    border: "1px solid color-mix(in srgb, var(--danger) 35%, transparent)",
    borderRadius: "6px",
    fontFamily: fonts.body,
    fontSize: "12px",
  },
  dismissButton: {
    alignSelf: "flex-start",
    marginTop: "10px",
    height: "28px",
    padding: "0 12px",
    backgroundColor: surfaceColors.paperDim,
    color: inkColors.ink2,
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "6px",
    fontFamily: fonts.body,
    fontSize: "11.5px",
    fontWeight: 600,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: surfaceColors.line,
    },
  },
});

export function HomeLoadProgress({
  docTitle,
  steps,
  error,
  done,
  header = "Opening document",
  errorHeader = "Couldn't open document",
  doneHeader,
  dismissLabel = "← Back to documents",
  onDismiss,
  percent,
}: HomeLoadProgressProps): React.ReactElement {
  const styles = useStyles();
  const hasError = !!error;
  const isDone = !hasError && !!done;
  const showBar = !hasError && percent != null;
  const barPct = Math.max(0, Math.min(100, percent ?? 0));

  return (
    <div
      className={styles.panel}
      role="status"
      aria-live="polite"
    >
      <p className={styles.title}>
        <span
          className={styles.titleIcon}
          aria-hidden="true"
        >
          {hasError ? "⚠" : isDone ? "✓" : "⧗"}
        </span>
        {hasError ? errorHeader : isDone ? (doneHeader ?? header) : header}
      </p>

      {docTitle ? <div className={styles.docTitle}>{docTitle}</div> : null}

      {steps.map((step) => (
        <div
          key={step.id}
          className={styles.step}
          data-step-state={hasError && step.state === "active" ? "error" : step.state}
        >
          <span
            aria-hidden="true"
            className={mergeClasses(
              styles.stepMarker,
              step.state === "done" && styles.stepMarkerDone,
              !hasError && step.state === "active" && styles.stepMarkerActive,
            )}
          />
          <span
            className={mergeClasses(
              styles.stepLabel,
              step.state === "pending" && styles.stepLabelPending,
              hasError && step.state === "active" && styles.stepLabelError,
            )}
          >
            {step.label}
          </span>
        </div>
      ))}

      {showBar && (
        <div
          className={styles.barTrack}
          role="progressbar"
          aria-valuenow={Math.round(barPct)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={styles.barFill}
            style={{ width: `${barPct}%` }}
          />
        </div>
      )}

      {hasError && (
        <div
          className={styles.errorRow}
          role="alert"
        >
          {error}
        </div>
      )}

      {hasError && onDismiss && (
        <button
          className={styles.dismissButton}
          onClick={onDismiss}
          type="button"
        >
          {dismissLabel}
        </button>
      )}
    </div>
  );
}
